/**
 * WebhookEventBrowserPage.tsx — Webhook event type catalog & payload inspector
 *
 * Features:
 *   - Browse all 36 webhook event types grouped by category
 *   - View JSON Schema for each event payload
 *   - See example payloads
 *   - Filter and search events
 *   - Copy payload schemas for integration development
 *
 * @module packages/billing-admin/src/pages/WebhookEventBrowserPage
 * @since 2026-06-20 (Platform Upgrade — Phase 3.8)
 */
import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useOrgContext } from '../contexts/OrgContext';
import AdminLayout from '../components/AdminLayout';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EventSchema {
  eventType: string;
  description: string;
  jsonSchema: Record<string, unknown>;
  examplePayload: Record<string, unknown>;
  version: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EVENT_CATEGORIES: Record<string, { label: string; color: string; icon: string }> = {
  customer: { label: 'Customer', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: '👤' },
  plan: { label: 'Plan', color: 'bg-indigo-100 text-indigo-700 border-indigo-200', icon: '📋' },
  subscription: { label: 'Subscription', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: '🔄' },
  invoice: { label: 'Invoice', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: '📄' },
  payment: { label: 'Payment', color: 'bg-green-100 text-green-700 border-green-200', icon: '💳' },
  usage: { label: 'Usage', color: 'bg-purple-100 text-purple-700 border-purple-200', icon: '📊' },
  credit: { label: 'Credit', color: 'bg-cyan-100 text-cyan-700 border-cyan-200', icon: '💰' },
  entitlement: { label: 'Entitlement', color: 'bg-teal-100 text-teal-700 border-teal-200', icon: '🔑' },
  dunning: { label: 'Dunning', color: 'bg-red-100 text-red-700 border-red-200', icon: '⚠️' },
  tenant: { label: 'Tenant', color: 'bg-slate-100 text-slate-700 border-slate-200', icon: '🏢' },
};

// Fallback static event catalog (used when API not yet available)
const STATIC_EVENTS: EventSchema[] = [
  { eventType: 'customer.created', description: 'Fired when a new customer is created', jsonSchema: {}, examplePayload: { id: 'cus_xxx', email: 'user@example.com', name: 'Jane Doe', created_at: '2026-06-20T10:00:00Z' }, version: 1 },
  { eventType: 'customer.updated', description: 'Fired when customer details are modified', jsonSchema: {}, examplePayload: { id: 'cus_xxx', changes: { name: 'Jane Smith' } }, version: 1 },
  { eventType: 'customer.deleted', description: 'Fired when a customer is removed', jsonSchema: {}, examplePayload: { id: 'cus_xxx', deleted_at: '2026-06-20T10:00:00Z' }, version: 1 },
  { eventType: 'subscription.created', description: 'Fired when a new subscription is started', jsonSchema: {}, examplePayload: { id: 'sub_xxx', customer_id: 'cus_xxx', plan_id: 'plan_xxx', status: 'active' }, version: 1 },
  { eventType: 'subscription.updated', description: 'Fired when subscription details change (upgrade/downgrade)', jsonSchema: {}, examplePayload: { id: 'sub_xxx', changes: { plan_id: 'plan_yyy' } }, version: 1 },
  { eventType: 'subscription.canceled', description: 'Fired when a subscription is canceled', jsonSchema: {}, examplePayload: { id: 'sub_xxx', canceled_at: '2026-06-20T10:00:00Z', effective_at: '2026-07-20T10:00:00Z' }, version: 1 },
  { eventType: 'subscription.paused', description: 'Fired when a subscription is paused', jsonSchema: {}, examplePayload: { id: 'sub_xxx', paused_at: '2026-06-20T10:00:00Z' }, version: 1 },
  { eventType: 'subscription.resumed', description: 'Fired when a paused subscription is resumed', jsonSchema: {}, examplePayload: { id: 'sub_xxx', resumed_at: '2026-06-20T10:00:00Z' }, version: 1 },
  { eventType: 'subscription.trial_ending', description: 'Fired 3 days before trial expiration', jsonSchema: {}, examplePayload: { id: 'sub_xxx', trial_ends_at: '2026-06-23T10:00:00Z' }, version: 1 },
  { eventType: 'invoice.created', description: 'Fired when a new invoice is generated', jsonSchema: {}, examplePayload: { id: 'inv_xxx', customer_id: 'cus_xxx', total_cents: 2900, status: 'draft' }, version: 1 },
  { eventType: 'invoice.finalized', description: 'Fired when an invoice is finalized and ready for payment', jsonSchema: {}, examplePayload: { id: 'inv_xxx', total_cents: 2900, status: 'finalized', due_date: '2026-07-01' }, version: 1 },
  { eventType: 'invoice.paid', description: 'Fired when an invoice is fully paid', jsonSchema: {}, examplePayload: { id: 'inv_xxx', paid_at: '2026-06-20T10:00:00Z', amount_paid_cents: 2900 }, version: 1 },
  { eventType: 'invoice.voided', description: 'Fired when an invoice is voided/canceled', jsonSchema: {}, examplePayload: { id: 'inv_xxx', voided_at: '2026-06-20T10:00:00Z', reason: 'duplicate' }, version: 1 },
  { eventType: 'invoice.overdue', description: 'Fired when an invoice passes its due date', jsonSchema: {}, examplePayload: { id: 'inv_xxx', due_date: '2026-06-15', days_overdue: 5 }, version: 1 },
  { eventType: 'payment.succeeded', description: 'Fired when a payment is successfully processed', jsonSchema: {}, examplePayload: { id: 'pay_xxx', amount_cents: 2900, method: 'card_visa_4242' }, version: 1 },
  { eventType: 'payment.failed', description: 'Fired when a payment attempt fails', jsonSchema: {}, examplePayload: { id: 'pay_xxx', amount_cents: 2900, failure_code: 'card_declined', failure_message: 'Your card was declined' }, version: 1 },
  { eventType: 'payment.refunded', description: 'Fired when a payment is refunded', jsonSchema: {}, examplePayload: { id: 'pay_xxx', refund_amount_cents: 2900, reason: 'customer_request' }, version: 1 },
  { eventType: 'payment.disputed', description: 'Fired when a payment is disputed (chargeback)', jsonSchema: {}, examplePayload: { id: 'pay_xxx', dispute_amount_cents: 2900, reason: 'fraudulent' }, version: 1 },
  { eventType: 'usage.threshold_reached', description: 'Fired when usage reaches a configured threshold percentage', jsonSchema: {}, examplePayload: { customer_id: 'cus_xxx', metric_key: 'api_calls', current: 8500, limit: 10000, percent: 85 }, version: 1 },
  { eventType: 'usage.limit_exceeded', description: 'Fired when usage exceeds the plan limit', jsonSchema: {}, examplePayload: { customer_id: 'cus_xxx', metric_key: 'api_calls', current: 10500, limit: 10000 }, version: 1 },
  { eventType: 'credit.granted', description: 'Fired when credits are added to a customer balance', jsonSchema: {}, examplePayload: { customer_id: 'cus_xxx', amount_cents: 5000, description: 'Promotional credit', new_balance_cents: 7500 }, version: 1 },
  { eventType: 'credit.depleted', description: 'Fired when a customer credit balance reaches zero', jsonSchema: {}, examplePayload: { customer_id: 'cus_xxx', last_usage_at: '2026-06-20T10:00:00Z' }, version: 1 },
  { eventType: 'entitlement.granted', description: 'Fired when access to a feature is granted', jsonSchema: {}, examplePayload: { customer_id: 'cus_xxx', feature_key: 'advanced_analytics', source: 'subscription' }, version: 1 },
  { eventType: 'entitlement.revoked', description: 'Fired when access to a feature is removed', jsonSchema: {}, examplePayload: { customer_id: 'cus_xxx', feature_key: 'advanced_analytics', reason: 'subscription_canceled' }, version: 1 },
  { eventType: 'entitlement.expired', description: 'Fired when a time-limited entitlement expires', jsonSchema: {}, examplePayload: { customer_id: 'cus_xxx', feature_key: 'trial_feature', expired_at: '2026-06-20T10:00:00Z' }, version: 1 },
  { eventType: 'dunning.started', description: 'Fired when dunning process begins for overdue payment', jsonSchema: {}, examplePayload: { customer_id: 'cus_xxx', invoice_id: 'inv_xxx', amount_cents: 2900 }, version: 1 },
  { eventType: 'dunning.retry_scheduled', description: 'Fired when a retry payment attempt is scheduled', jsonSchema: {}, examplePayload: { customer_id: 'cus_xxx', retry_at: '2026-06-22T10:00:00Z', attempt: 2 }, version: 1 },
  { eventType: 'dunning.recovered', description: 'Fired when a dunning payment is successfully recovered', jsonSchema: {}, examplePayload: { customer_id: 'cus_xxx', recovered_amount_cents: 2900, attempt: 3 }, version: 1 },
  { eventType: 'dunning.failed', description: 'Fired when all dunning retries are exhausted', jsonSchema: {}, examplePayload: { customer_id: 'cus_xxx', total_attempts: 4, final_status: 'write_off' }, version: 1 },
  { eventType: 'tenant.provisioned', description: 'Fired when a new tenant is fully provisioned', jsonSchema: {}, examplePayload: { tenant_id: 'ten_xxx', name: 'Acme Corp', status: 'active' }, version: 1 },
  { eventType: 'tenant.suspended', description: 'Fired when a tenant is suspended', jsonSchema: {}, examplePayload: { tenant_id: 'ten_xxx', reason: 'payment_overdue', suspended_at: '2026-06-20T10:00:00Z' }, version: 1 },
  { eventType: 'tenant.reactivated', description: 'Fired when a suspended tenant is reactivated', jsonSchema: {}, examplePayload: { tenant_id: 'ten_xxx', reactivated_at: '2026-06-20T10:00:00Z' }, version: 1 },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function WebhookEventBrowserPage(): React.JSX.Element {
  const { token, apiBaseUrl } = useAdminAuth();
  const { activeOrg } = useOrgContext();
  const orgId = activeOrg?.orgId ?? '';

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<EventSchema | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Fetch event schemas from API (falls back to static catalog)
  const { data: events } = useQuery<EventSchema[]>({
    queryKey: ['org', orgId, 'webhook-event-schemas'],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/api/v1/orgs/${orgId}/docs/integration/events`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return STATIC_EVENTS;
      const body = await res.json();
      return body.events ?? STATIC_EVENTS;
    },
    enabled: !!token && !!orgId,
    placeholderData: STATIC_EVENTS,
  });

  // ─── Filtering ─────────────────────────────────────────────────────────
  const filteredEvents = useMemo(() => {
    let result = events ?? STATIC_EVENTS;
    if (selectedCategory) {
      result = result.filter((e) => e.eventType.startsWith(selectedCategory + '.'));
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter((e) =>
        e.eventType.toLowerCase().includes(term) ||
        e.description.toLowerCase().includes(term)
      );
    }
    return result;
  }, [events, selectedCategory, searchTerm]);

  // Group by category for count badges
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const e of (events ?? STATIC_EVENTS)) {
      const cat = e.eventType.split('.')[0];
      counts[cat] = (counts[cat] ?? 0) + 1;
    }
    return counts;
  }, [events]);

  // ─── Copy helper ───────────────────────────────────────────────────────
  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <AdminLayout title="Webhook Events">
      <div className="flex gap-6 h-[calc(100vh-8rem)]">
        {/* ─── Category Sidebar ───────────────────────────────────────── */}
        <div className="w-56 flex-shrink-0 space-y-1">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`w-full rounded-lg px-3 py-2 text-left text-sm transition-colors ${
              !selectedCategory ? 'bg-cyan-50 text-cyan-700 font-medium' : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            All Events
            <span className="ml-2 text-xs text-slate-400">{(events ?? STATIC_EVENTS).length}</span>
          </button>
          {Object.entries(EVENT_CATEGORIES).map(([key, { label, icon }]) => (
            <button
              key={key}
              onClick={() => setSelectedCategory(key)}
              className={`w-full rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                selectedCategory === key ? 'bg-cyan-50 text-cyan-700 font-medium' : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <span className="mr-2">{icon}</span>
              {label}
              <span className="ml-2 text-xs text-slate-400">{categoryCounts[key] ?? 0}</span>
            </button>
          ))}
        </div>

        {/* ─── Event List ─────────────────────────────────────────────── */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Search */}
          <div className="mb-4">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search events (e.g. invoice.paid, subscription)..."
              className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm placeholder:text-slate-400 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
            />
          </div>

          <div className="flex-1 overflow-y-auto rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
            {filteredEvents.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <p className="text-sm text-slate-400">No events match your filter</p>
              </div>
            ) : (
              filteredEvents.map((event) => {
                const category = event.eventType.split('.')[0];
                const catMeta = EVENT_CATEGORIES[category];
                return (
                  <button
                    key={event.eventType}
                    onClick={() => setSelectedEvent(event)}
                    className={`w-full px-4 py-3 text-left transition-colors hover:bg-slate-50 ${
                      selectedEvent?.eventType === event.eventType ? 'bg-cyan-50' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <code className="text-sm font-medium text-slate-800">{event.eventType}</code>
                      {catMeta && (
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium border ${catMeta.color}`}>
                          {catMeta.label}
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-slate-500 truncate">{event.description}</p>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* ─── Event Detail Panel ─────────────────────────────────────── */}
        <div className="w-96 flex-shrink-0 overflow-y-auto rounded-xl border border-slate-200 bg-white">
          {!selectedEvent ? (
            <div className="flex flex-col items-center justify-center h-full px-6 text-center">
              <p className="text-sm font-medium text-slate-700">Select an event</p>
              <p className="mt-1 text-xs text-slate-400">Click an event to see its payload schema and example.</p>
            </div>
          ) : (
            <div className="p-5 space-y-5">
              <div>
                <code className="text-base font-semibold text-slate-900">{selectedEvent.eventType}</code>
                <p className="mt-1 text-sm text-slate-600">{selectedEvent.description}</p>
                <p className="mt-1 text-xs text-slate-400">Schema version: {selectedEvent.version}</p>
              </div>

              {/* Example Payload */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs font-medium text-slate-600">Example Payload</p>
                  <button
                    onClick={() => copyToClipboard(JSON.stringify(selectedEvent.examplePayload, null, 2), 'example')}
                    className="text-[10px] text-cyan-600 hover:text-cyan-700"
                  >
                    {copiedField === 'example' ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
                <pre className="rounded-lg bg-slate-900 text-slate-100 p-3 text-xs font-mono overflow-auto max-h-60">
                  {JSON.stringify(selectedEvent.examplePayload, null, 2)}
                </pre>
              </div>

              {/* JSON Schema (if available) */}
              {Object.keys(selectedEvent.jsonSchema).length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-xs font-medium text-slate-600">JSON Schema</p>
                    <button
                      onClick={() => copyToClipboard(JSON.stringify(selectedEvent.jsonSchema, null, 2), 'schema')}
                      className="text-[10px] text-cyan-600 hover:text-cyan-700"
                    >
                      {copiedField === 'schema' ? '✓ Copied' : 'Copy'}
                    </button>
                  </div>
                  <pre className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs font-mono overflow-auto max-h-48">
                    {JSON.stringify(selectedEvent.jsonSchema, null, 2)}
                  </pre>
                </div>
              )}

              {/* Integration snippet */}
              <div>
                <p className="text-xs font-medium text-slate-600 mb-1.5">Webhook Handler Example</p>
                <pre className="rounded-lg bg-slate-900 text-green-300 p-3 text-xs font-mono overflow-auto max-h-48">
{`app.post('/webhooks/billing', (req, res) => {
  const event = req.body;
  
  if (event.type === '${selectedEvent.eventType}') {
    // Handle ${selectedEvent.eventType}
    const payload = event.data;
    console.log('Received:', payload);
    // ... your logic here
  }
  
  res.status(200).json({ received: true });
});`}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
